// Slice do role play falado (spec-018) — a sessão em curso e o histórico.
//
// Mora em arquivo próprio, como `premissas.js`, e é MESCLADO no namespace
// `feeFixo` por `store/index.js`: as chaves ficam planas (`feeFixo/roleplaySessao`).
//
// Quem carrega e grava é `api/apiFeeFixoRoleplay`; as actions daqui só commitam,
// que é o padrão do módulo e o que o review cobra ([#1352, #1358, #1400]).
//
// ⚠️ `etapa` é estado de UI, não dado do backend: é ela que faz a tela dizer
// `gravando → transcrevendo → cliente pensando → falando`. A [US-2] exige o
// estado visível por etapa porque o turno leva de 3 a 6 segundos medidos, e 5
// segundos sem retorno na tela parecem travamento.

const slice = (data) => ({ loading: false, lastUpdate: null, data })

const gravar = (slice, payload) => {
  if (payload !== undefined) {
    slice.data = payload
    slice.lastUpdate = new Date()
  }
}

// As etapas do turno, na ordem em que acontecem. `ocioso` é o estado entre
// turnos; `falhou` guarda em qual etapa parou, que é o que a tela mostra para o
// consultor repetir o turno certo.
export const ETAPA_OCIOSO = 'ocioso'
export const ETAPA_GRAVANDO = 'gravando'
export const ETAPA_TRANSCREVENDO = 'transcrevendo'
export const ETAPA_PENSANDO = 'pensando'
export const ETAPA_FALANDO = 'falando'

export const state = () => ({
  // Sessão em curso — o `value` de `POST /roleplay/sessoes`, com ficha, tupla,
  // voice_id e o aviso de voz quando o mapa está desligado.
  roleplaySessao: slice(null),
  // Turnos na ordem, como o backend os devolve.
  roleplayTurnos: slice([]),
  // Avaliação da sessão encerrada. `null` é resposta legítima: sessão
  // abandonada e avaliação que falhou não têm nota, e um zero no lugar seria
  // uma afirmação falsa sobre o desempenho do consultor.
  roleplayNota: slice(null),
  // Histórico de ensaios (do consultor, ou do time quando gestor — quem decide
  // é o backend; a tela não filtra nada, porque tela não é barreira de tenancy).
  roleplayHistorico: slice([]),
  // Quota do mês do escritório — e, desde a spec-023, o limite da descrição
  // livre (`limite_prompt_livre`). O limite viaja AQUI, e não numa constante
  // do front, porque quem o cobra é o backend: uma cópia local divergiria em
  // silêncio e o contador da tela prometeria espaço que a criação recusa.
  roleplayQuota: slice(null),
  roleplayEtapa: ETAPA_OCIOSO,
  roleplayErro: '',
})

export const getters = {
  roleplaySessao: (state) => state.roleplaySessao.data,
  roleplaySessaoCarregando: (state) => state.roleplaySessao.loading,
  roleplayTurnos: (state) => state.roleplayTurnos.data || [],
  roleplayNota: (state) => state.roleplayNota.data,
  roleplayHistorico: (state) => state.roleplayHistorico.data || [],
  roleplayHistoricoCarregando: (state) => state.roleplayHistorico.loading,
  roleplayQuota: (state) => state.roleplayQuota.data || {},
  // `null` quando a quota ainda não respondeu — e a tela trata isso como
  // "sem contador", nunca como zero. Contador com número inventado seria
  // pior que contador nenhum.
  roleplayLimitePromptLivre: (state) => {
    const quota = state.roleplayQuota.data
    if (!quota || !quota.limite_prompt_livre) return null
    return quota.limite_prompt_livre
  },
  roleplayEtapa: (state) => state.roleplayEtapa,
  roleplayErro: (state) => state.roleplayErro,
  // "Já existe sessão aberta?" — distingue tela sem sessão de sessão vazia.
  roleplaySessaoAberta: (state) =>
    !!(state.roleplaySessao.data && state.roleplaySessao.data.id),
}

export const mutations = {
  SET_ROLEPLAY_SESSAO: (state, payload) => gravar(state.roleplaySessao, payload),
  SET_ROLEPLAY_SESSAO_LOADING: (state, payload) => {
    state.roleplaySessao.loading = !!payload
  },
  SET_ROLEPLAY_TURNOS: (state, payload) =>
    gravar(state.roleplayTurnos, Array.isArray(payload) ? payload : []),
  SET_ROLEPLAY_NOTA: (state, payload) => gravar(state.roleplayNota, payload),
  SET_ROLEPLAY_HISTORICO: (state, payload) =>
    gravar(state.roleplayHistorico, Array.isArray(payload) ? payload : []),
  SET_ROLEPLAY_HISTORICO_LOADING: (state, payload) => {
    state.roleplayHistorico.loading = !!payload
  },
  SET_ROLEPLAY_QUOTA: (state, payload) => gravar(state.roleplayQuota, payload),
  SET_ROLEPLAY_ETAPA: (state, payload) => {
    state.roleplayEtapa = payload || ETAPA_OCIOSO
  },
  SET_ROLEPLAY_ERRO: (state, payload) => {
    state.roleplayErro = payload || ''
  },
}

export const actions = {
  setRoleplaySessao: ({ commit }, payload) =>
    commit('SET_ROLEPLAY_SESSAO', payload),
  setRoleplaySessaoLoading: ({ commit }, payload) =>
    commit('SET_ROLEPLAY_SESSAO_LOADING', payload),
  setRoleplayTurnos: ({ commit }, payload) =>
    commit('SET_ROLEPLAY_TURNOS', payload),
  setRoleplayNota: ({ commit }, payload) => commit('SET_ROLEPLAY_NOTA', payload),
  setRoleplayQuota: ({ commit }, payload) =>
    commit('SET_ROLEPLAY_QUOTA', payload),
  setRoleplayHistorico: ({ commit }, payload) =>
    commit('SET_ROLEPLAY_HISTORICO', payload),
  setRoleplayHistoricoLoading: ({ commit }, payload) =>
    commit('SET_ROLEPLAY_HISTORICO_LOADING', payload),
  setRoleplayEtapa: ({ commit }, payload) =>
    commit('SET_ROLEPLAY_ETAPA', payload),
  setRoleplayErro: ({ commit }, payload) => commit('SET_ROLEPLAY_ERRO', payload),
}
